import { useRef } from 'react';
/**
 * Builds a portal's `useFeatureFlagsSelector` hook on top of its `useFeatureFlags` hook.
 */
export function createUseFeatureFlagsSelector(useFeatureFlags) {
    /**
     * Subscribes to a *derived slice* of feature flags using a selector function.
     *
     * The selector receives the full feature flag object and should return
     * the specific value or structure that the component depends on.
     *
     * The hook ensures referential stability by comparing the previous and
     * next selector results using the provided `isEqual` function.
     * If the selected value has not changed, the consuming component will
     * not rerender even if unrelated flags update.
     *
     * ✅ Use this hook when:
     * - you need multiple flags
     * - you need derived logic based on flags
     * - you want to minimise unnecessary rerenders
     * - building domain-level flag abstractions
     *
     * Example:
     * ```ts
     * const isNewCheckoutFlow = useFeatureFlagsSelector(
     *   flags => flags.newCheckout && !flags.legacyPayments
     * );
     * ```
     *
     * ⚠️ Important:
     * The result from the selector should be referentially stable (eg: use useCallback if you are returning an object from an inlined selector function)
     * OR if it cant be stable pass a custom isEqual function instead
     *
     * @param selector - Function that selects the subset or derived flags.
     * @param isEqual - Optional function to compare previous and next selected values.
     * @returns The selected feature flag(s) with stable reference.
     */
    return function useFeatureFlagsSelector(selector, isEqual = Object.is) {
        // Writing to ref.current during render is intentional here (custom-equality memoization
        // without an extra render pass), but React Compiler assumes render is pure and may
        // miscompile it, so this hook is opted out of compilation.
        'use no memo';
        const { flags } = useFeatureFlags();
        const selected = selector(flags);
        const ref = useRef(selected);
        if (!isEqual(ref.current, selected)) {
            ref.current = selected;
        }
        return ref.current;
    };
}
//# sourceMappingURL=data:application/json;base64,eyJ2ZXJzaW9uIjozLCJmaWxlIjoiY3JlYXRlVXNlRmVhdHVyZUZsYWdzU2VsZWN0b3IuanMiLCJzb3VyY2VSb290IjoiIiwic291cmNlcyI6WyIuLi8uLi8uLi9zcmMvaG9va3MvZmVhdHVyZUZsYWdzL2NyZWF0ZVVzZUZlYXR1cmVGbGFnc1NlbGVjdG9yLnRzIl0sIm5hbWVzIjpbXSwibWFwcGluZ3MiOiJBQUFBLE9BQU8sRUFBRSxNQUFNLEVBQUUsTUFBTSxPQUFPLENBQUM7QUFPL0I7O0dBRUc7QUFDSCxNQUFNLFVBQVUsNkJBQTZCLENBQzNDLGVBQXdDO0lBRXhDOzs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7O09BK0JHO0lBQ0gsT0FBTyxTQUFTLHVCQUF1QixDQUNyQyxRQUE4QixFQUM5QixVQUFtQyxNQUFNLENBQUMsRUFBRTtRQUU1Qyx3RkFBd0Y7UUFDeEYsbUZBQW1GO1FBQ25GLDJEQUEyRDtRQUMzRCxhQUFhLENBQUM7UUFFZCxNQUFNLEVBQUUsS0FBSyxFQUFFLEdBQUcsZUFBZSxFQUFFLENBQUM7UUFFcEMsTUFBTSxRQUFRLEdBQUcsUUFBUSxDQUFDLEtBQUssQ0FBQyxDQUFDO1FBRWpDLE1BQU0sR0FBRyxHQUFHLE1BQU0sQ0FBQyxRQUFRLENBQUMsQ0FBQztRQUU3QixJQUFJLENBQUMsT0FBTyxDQUFDLEdBQUcsQ0FBQyxPQUFPLEVBQUUsUUFBUSxDQUFDLEVBQUUsQ0FBQztZQUNwQyxHQUFHLENBQUMsT0FBTyxHQUFHLFFBQVEsQ0FBQztRQUN6QixDQUFDO1FBRUQsT0FBTyxHQUFHLENBQUMsT0FBTyxDQUFDO0lBQ3JCLENBQUMsQ0FBQztBQUNKLENBQUMifQ==